import ModelTypeConstants from 'constants/ModelTypeConstants';
import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import type {PipelineStepRawData} from './PipelineTypes';

/** The GraphQL `__typename` of a raw step data union member. */
type RawStepDataTypename = PipelineStepRawData['__typename'];

/**
 * The already-resolved facts about a view that a kind's `matchesView` override
 * may need. Passed in (rather than the view itself) so this module stays in the
 * `pipelines` layer with no dependency on the view-resolution utilities, which
 * would otherwise create an import cycle.
 */
export interface ViewKindMatchContext {
  /** The kind of the view's output raw data, or null if it has no output data. */
  outputDataKind: PipelineStepDataKind | null;
  /** The step type of the view's last lineage node, if resolvable. */
  lastStepType: PipelineStepType | null;
}

/**
 * One raw step data kind's metadata and behavior.
 *
 * The data fields (`typename`, `icon`) cover the common case; the optional
 * methods are overridable hooks for kinds that need special behavior. When a
 * method is omitted, the shared helpers apply the documented default, so most
 * kinds only need to declare data.
 */
export interface RawStepDataKind {
  /** The GraphQL `__typename` of the union member for this kind. */
  typename: RawStepDataTypename;
  /** Icon used to represent this kind of data (e.g. in step-data chips). */
  icon: BenchlingIconName;
  /** Icon shown for a succeeded view that outputs this kind of data. */
  viewIcon: BenchlingIconName;
  /**
   * Whether a view "is" this kind for selection/filtering purposes.
   *
   * Default (when omitted): the view's output data is of this kind. Override
   * for kinds that match additional views (e.g. datasets also match chart
   * views, which are remappable to their output data frame).
   */
  matchesView?(context: ViewKindMatchContext): boolean;
}

const RAW_STEP_DATA_KIND_REGISTRY: Partial<Record<PipelineStepDataKind, RawStepDataKind>> = {
  [PipelineStepDataKind.CHART]: {
    typename: 'ChartNode',
    icon: 'analyze',
    // These model-type values double as icon names (resolved via icon aliases).
    viewIcon: ModelTypeConstants.CHART as BenchlingIconName,
  },
  [PipelineStepDataKind.DATASET]: {
    typename: 'DataFrameDomainNode',
    icon: 'table',
    viewIcon: ModelTypeConstants.DATA_FRAME as BenchlingIconName,
    // Datasets also match chart views, which can be remapped to their output
    // data frame (see mapChartViewsToDataFrameViews).
    matchesView: ({outputDataKind, lastStepType}) =>
      outputDataKind === PipelineStepDataKind.DATASET || lastStepType === PipelineStepType.CHART_VIEW,
  },
  [PipelineStepDataKind.FILE]: {
    typename: 'ScratchFileNode',
    icon: 'paperclip',
    viewIcon: ModelTypeConstants.FILE as BenchlingIconName,
  },
  [PipelineStepDataKind.PLATE]: {
    typename: 'ScratchPlateNode',
    icon: 'well-plate',
    viewIcon: ModelTypeConstants.SCRATCH_PLATE as BenchlingIconName,
  },
};

// Reverse lookup (__typename -> kind), precomputed once so that resolving a raw
// data's kind on the render path is O(1) rather than a linear scan per call.
const TYPENAME_TO_KIND: Partial<Record<RawStepDataTypename, PipelineStepDataKind>> = Object.fromEntries(
  (Object.keys(RAW_STEP_DATA_KIND_REGISTRY) as Array<PipelineStepDataKind>).map((kind) => [
    RAW_STEP_DATA_KIND_REGISTRY[kind]!.typename,
    kind,
  ])
);

/** Return the registered entry for a kind, or `undefined` if none is registered. */
export function getRawStepDataKind(kind: PipelineStepDataKind): RawStepDataKind | undefined {
  return RAW_STEP_DATA_KIND_REGISTRY[kind];
}

/** Return the GraphQL `__typename` for a kind, or `undefined` if the kind is not registered. */
export function getTypenameForStepDataKind(kind: PipelineStepDataKind): RawStepDataTypename | undefined {
  return RAW_STEP_DATA_KIND_REGISTRY[kind]?.typename;
}

/** Return the icon for a kind, or `undefined` if the kind is not registered. */
export function getIconForStepDataKindOrUndefined(kind: PipelineStepDataKind): BenchlingIconName | undefined {
  return RAW_STEP_DATA_KIND_REGISTRY[kind]?.icon;
}

/** Return the view icon for a kind, or `undefined` if the kind is not registered. */
export function getViewIconForStepDataKind(kind: PipelineStepDataKind): BenchlingIconName | undefined {
  return RAW_STEP_DATA_KIND_REGISTRY[kind]?.viewIcon;
}

/**
 * Resolve a raw step data's kind from its GraphQL `__typename`, or `undefined`
 * if no registered kind matches. Lets consumers key behavior (e.g. which view
 * component to render) off a single map lookup instead of a chain of
 * per-kind type guards.
 */
export function getStepDataKindForRawData(rawData: {__typename: string}): PipelineStepDataKind | undefined {
  return TYPENAME_TO_KIND[rawData.__typename as RawStepDataTypename];
}

/**
 * Whether a view (described by `context`) matches the given kind, applying the
 * kind's `matchesView` override or, by default, that the view's output data is
 * of this kind. Returns `false` for an unregistered kind.
 */
export function viewMatchesStepDataKind(kind: PipelineStepDataKind, context: ViewKindMatchContext): boolean {
  const entry = RAW_STEP_DATA_KIND_REGISTRY[kind];
  if (entry == null) {
    return false;
  }
  if (entry.matchesView != null) {
    return entry.matchesView(context);
  }
  return context.outputDataKind === kind;
}
